import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tag, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { customersApi } from "@/lib/admin-api/crm";
import type { CustomerTag } from "@/lib/admin-api/crm";
import { ApiError } from "@/lib/api-error-handler";

// ---------------------------------------------------------------------------
// TagsPanel — live tag chips with add/remove
// ---------------------------------------------------------------------------

export function TagsPanel({ customerId }: { readonly customerId: string }) {
  const queryClient = useQueryClient();
  const [newTag, setNewTag] = useState("");

  const { data: tags = [] } = useQuery<readonly CustomerTag[]>({
    queryKey: ["admin", "customer-tags", customerId],
    queryFn: () => customersApi.tags.list(customerId),
    staleTime: 30_000,
  });

  const addMutation = useMutation({
    mutationFn: (tag: string) => customersApi.tags.add(customerId, tag),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "customer-tags", customerId] });
      setNewTag("");
      toast.success("Tag ajouté");
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.toHumanMessage() : "Impossible d'ajouter le tag"),
  });

  const removeMutation = useMutation({
    mutationFn: (tag: string) => customersApi.tags.remove(customerId, tag),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "customer-tags", customerId] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.toHumanMessage() : "Impossible de supprimer le tag"),
  });

  return (
    <div className="space-y-3 rounded-xl border border-stone-200 bg-white p-5">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-stone-700">
        <Tag className="h-4 w-4 text-stone-400" />
        Tags
      </h3>
      <div className="flex flex-wrap gap-2">
        {tags.length === 0 && (
          <span className="text-xs italic text-stone-400">Aucun tag</span>
        )}
        {tags.map((t) => (
          <span
            key={t.tag}
            className="inline-flex items-center gap-1 rounded bg-stone-100 px-2 py-0.5 text-xs text-stone-700"
          >
            {t.tag}
            <button
              type="button"
              disabled={removeMutation.isPending}
              onClick={() => removeMutation.mutate(t.tag)}
              aria-label={`Retirer le tag ${t.tag}`}
              className="text-stone-400 hover:text-red-600"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const trimmed = newTag.trim();
          if (trimmed.length > 0 && trimmed.length <= 50) {
            addMutation.mutate(trimmed);
          }
        }}
        className="flex gap-2"
      >
        <input
          type="text"
          maxLength={50}
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          placeholder="Nouveau tag"
          className="flex-1 rounded border border-stone-200 bg-white px-2 py-1 text-xs focus:border-amber-400 focus:outline-none"
        />
        <button
          type="submit"
          disabled={addMutation.isPending || newTag.trim().length === 0}
          className="inline-flex items-center gap-1 rounded border border-stone-200 bg-stone-50 px-2 py-1 text-xs font-medium text-stone-700 hover:bg-stone-100 disabled:opacity-50"
        >
          <Plus className="h-3 w-3" />
          Ajouter
        </button>
      </form>
    </div>
  );
}
